"use client";

import { useStrictLocalization } from "@faire/localization/react/useStrictLocalization";
import { componentCls } from "@faire/slate/lib/tracking";
import { SpinnerBase } from "@faire/slate/private/loading-spinner/SpinnerBase";
import {
  SpinnerSize,
  ISpinnerWebProps,
} from "@faire/slate/private/loading-spinner/type";
import React from "react";

type IProps = {
  /**
   * The color of the spinner.
   *
   * @default slate-icon-default
   */
  color?: string;

  /**
   * The size of the spinner.
   * - `xSmall`: 12px
   * - `small`: 16px
   * - `medium`: 24px
   * - `large`: 32px
   * - `xLarge`: 48px
   *
   * @default small
   */
  size?: SpinnerSize;

  // DO NOT ADD any new prop without aligning with Slate team first
} & ISpinnerWebProps;

/**
 * A loading spinner represents an unspecified loading time.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Loading-Spinner-2242efb5c25a8075b517f649813528cc | Slate/LoadingSpinner}
 */
export const LoadingSpinner: React.FC<IProps> = ({
  size = "small",
  ...props
}) => {
  const { strictLocalize } = useStrictLocalization();
  return (
    <SpinnerBase
      {...props}
      aria-label={strictLocalize({
        id: "LJ8sQp",
        defaultMessage: "Loading",
        description: {
          text: "Message for screen readers when buttons just show a loading spinner with no text",
        },
      })}
      className={componentCls()}
      data-component-name="Slate/LoadingSpinner"
      role="status"
      size={size}
    />
  );
};
